import { isLocalModeSubmitParams, LOCAL_MODEL_PREFERENCE, } from '#pplx/frontend/apps/ask/thread-utils/computerThreadActions.js';
import { parseUrl } from '#pplx/frontend/common/files/urls.js';
import { logger } from '#pplx/frontend/logger/browser.js';
let submissionCount = 0;
const REDACTED_QUERY_PLACEHOLDER = '<redacted>';
class TimeMarker {
    eventTime = -1;
    setOnce() {
        if (this.eventTime == -1) {
            this.eventTime = Date.now();
            return true;
        }
        return false;
    }
    timeSinceTimestamp(oldTimestamp) {
        if (this.eventTime === -1 || oldTimestamp == null) {
            return -1;
        }
        else if (this.eventTime < oldTimestamp) {
            logger.log(`TimeMarker.timeSinceTimestamp found inconsistency: mytime ${this.eventTime}, old: ${oldTimestamp}`);
            return -1;
        }
        return this.eventTime - oldTimestamp;
    }
    timeBeforeTimestamp(nextTimestamp) {
        if (this.eventTime === -1 || nextTimestamp == null) {
            return -1;
        }
        else if (this.eventTime > nextTimestamp) {
            logger.log(`TimeMarker.timeBeforeTimestamp found inconsistency: mytime ${this.eventTime}, next: ${nextTimestamp}`);
            return -1;
        }
        return nextTimestamp - this.eventTime;
    }
    wasSet() {
        return this.eventTime != -1;
    }
}
const DEFAULT_SUBMISSION_EVENT_DATA = {
    firstServerResponseReceived: false,
    firstLLMTokenReceived: false,
    isPerplexityBrowser: false,
};
// This records timing data for any query made over the lifetime of our web
// app. It is a known leak but relatively small, and used for debugging mode.
const submissionEventDataMap = new Map();
export const handleStreamProgress = (id, { hasLLMToken, hasSearchResults, hasSources, }, track) => {
    _maybeReceiveFirstServerResponse(id, track);
    _maybeReceiveFirstLLMToken(id, hasLLMToken, track);
    _maybeReceiveFirstSearchResults(id, hasSearchResults);
    _maybeReceiveFirstSources(id, hasSources);
};
const _maybeReceiveFirstServerResponse = (id, track) => {
    const submissionEventData = submissionEventDataMap.get(id);
    if (!submissionEventData) {
        return;
    }
    if (submissionEventData.firstServerResponseReceived) {
        return;
    }
    if (!submissionEventData.submitTimestamp) {
        return;
    }
    if (!submissionEventData.startFirstServerResponseMarker?.setOnce()) {
        return;
    }
    submissionEventData.firstServerResponseReceived = true;
    const { queryStr, isFollowup, queryParams } = submissionEventData;
    const startFirstServerResponseElapsed = getStartFirstServerResponseElapsed(id);
    if (startFirstServerResponseElapsed > 0) {
        track({
            event: 'query first server response',
            data: {
                startFirstServerResponseElapsed,
                queryStr,
                isFollowup,
                mode: queryParams?.mode ?? 'unknown',
                isPerplexityBrowser: submissionEventData.isPerplexityBrowser,
            },
        });
    }
};
const _maybeReceiveFirstLLMToken = (id, hasLLMToken, track) => {
    const submissionEventData = submissionEventDataMap.get(id);
    if (!submissionEventData) {
        return;
    }
    if (submissionEventData.firstLLMTokenReceived) {
        return;
    }
    if (!hasLLMToken) {
        return;
    }
    if (!submissionEventData.startLLMTokenMarker?.setOnce()) {
        return;
    }
    submissionEventData.firstLLMTokenReceived = true;
    const { isFollowup, queryParams } = submissionEventData;
    const startLLMTokenElapsed = getStartLLMTokenElapsed(id);
    if (startLLMTokenElapsed > 0) {
        track({
            event: 'query first llm token',
            data: {
                startLLMTokenElapsed,
                isFollowup,
                mode: queryParams?.mode ?? 'unknown',
                isPerplexityBrowser: submissionEventData.isPerplexityBrowser,
            },
        });
    }
};
const _maybeReceiveFirstSearchResults = (id, hasSearchResult) => {
    const submissionEventData = submissionEventDataMap.get(id);
    if (!submissionEventData) {
        return;
    }
    else if (!hasSearchResult) {
        return;
    }
    submissionEventData.firstSearchResultsMarker?.setOnce();
};
const _maybeReceiveFirstSources = (id, hasSources) => {
    const submissionEventData = submissionEventDataMap.get(id);
    if (!submissionEventData) {
        return;
    }
    else if (!hasSources) {
        return;
    }
    submissionEventData.firstSourcesMarker?.setOnce();
};
export const endStreamResponse = (id, { track, name, }) => {
    const submissionEventData = submissionEventDataMap.get(id);
    if (!submissionEventData) {
        return;
    }
    // We should never call `endStreamResponse` twice,
    // but guard against it just in caase
    if (submissionEventData.endStreamTimestamp) {
        return;
    }
    submissionEventData.endStreamTimestamp = Date.now();
    const totalElapsedData = submissionEventData.submitTimestamp == null
        ? {}
        : {
            totalElapsed: submissionEventData.endStreamTimestamp - submissionEventData.submitTimestamp,
        };
    track({
        event: name, // event names are historical
        data: {
            ...submissionEventData,
            ...totalElapsedData,
        },
    });
};
const getTabHost = (url) => {
    return parseUrl(url)?.host ?? '';
};
const LOCAL_MODE_QUERY_PARAM_ALLOWLIST = [
    'is_incognito',
    'is_local_browser_available',
    'language',
    'local_search_enabled',
    'mode',
    'model_preference',
    'prompt_source',
    'query_source',
    'search_focus',
    'skip_search_enabled',
    'source',
    'time_from_first_type',
    'timezone',
    'workflow_key',
];
const redactUserMentionQueryParams = (queryParams) => {
    const redacted = {
        ...queryParams,
    };
    delete redacted.dsl_query;
    delete redacted.mentions;
    delete redacted.query_display_metadata;
    return redacted;
};
const sanitizeAnalyticsQueryParams = (queryParams) => {
    const analyticsQueryParams = { ...queryParams };
    delete analyticsQueryParams.dsl_query;
    const metadata = analyticsQueryParams.query_display_metadata;
    if (metadata && typeof metadata === 'object' && !Array.isArray(metadata)) {
        // Keep optimistic identity on the request, but never forward it to analytics.
        const displayMetadata = { ...metadata };
        delete displayMetadata.sender;
        analyticsQueryParams.query_display_metadata = displayMetadata;
    }
    return analyticsQueryParams;
};
const redactSensitiveQueryParams = (queryParams) => {
    const params = queryParams;
    const redacted = {};
    LOCAL_MODE_QUERY_PARAM_ALLOWLIST.forEach((field) => {
        if (params[field] !== undefined) {
            redacted[field] = params[field];
        }
    });
    return redacted;
};
const isLocalModeQuery = (queryParams) => {
    return isLocalModeSubmitParams(queryParams);
};
export const isFollowupQuerySource = (querySource) => querySource === 'followup' ||
    querySource === 'followup-sidecar' ||
    querySource === 'followup-mobile-sidecar' ||
    querySource === 'computer-followup';
const getAnalyticsQueryParams = ({ queryParams, analyticsPolicy, isLocalMode, hasMentionedUsers, }) => {
    if (isLocalMode) {
        return {
            ...redactSensitiveQueryParams(queryParams),
            model_preference: LOCAL_MODEL_PREFERENCE,
        };
    }
    if (analyticsPolicy === 'redact-query-content') {
        return redactSensitiveQueryParams(queryParams);
    }
    if (hasMentionedUsers) {
        return redactUserMentionQueryParams(queryParams);
    }
    return sanitizeAnalyticsQueryParams(queryParams);
};
export const trackSubmitQuery = (id, { analyticsPolicy, name, queryStr, queryParams, isFollowup, frontendUUID, isPerplexityBrowser, track, browserVersion, connectors, mentions, inlineAssistantAction, }) => {
    submissionCount += 1;
    const mentionedUsers = mentions?.filter((item) => item.type === 'user') ?? [];
    const isLocalMode = isLocalModeQuery(queryParams);
    const hasMentionedUsers = mentionedUsers.length > 0;
    const shouldRedactQueryContent = hasMentionedUsers || isLocalMode || analyticsPolicy === 'redact-query-content';
    const submissionEventData = { ...DEFAULT_SUBMISSION_EVENT_DATA };
    submissionEventData.submissionType = name;
    submissionEventData.submitTimestamp = Date.now();
    // The request keeps its person-resolution fields, but analytics must not
    // receive user-authored local prompts or selected coworker identity.
    submissionEventData.queryStr = shouldRedactQueryContent ? REDACTED_QUERY_PLACEHOLDER : queryStr;
    submissionEventData.queryParams = getAnalyticsQueryParams({
        queryParams,
        analyticsPolicy,
        isLocalMode,
        hasMentionedUsers,
    });
    submissionEventData.frontendUUID = frontendUUID;
    submissionEventData.submissionCount = submissionCount;
    submissionEventData.isFollowup = isFollowup;
    submissionEventData.startFirstServerResponseMarker = new TimeMarker();
    submissionEventData.startLLMTokenMarker = new TimeMarker();
    submissionEventData.firstSearchResultsMarker = new TimeMarker();
    submissionEventData.firstSourcesMarker = new TimeMarker();
    submissionEventData.firstSourcesRenderMarker = new TimeMarker();
    submissionEventData.isPerplexityBrowser = isPerplexityBrowser;
    submissionEventData.browserVersion = browserVersion;
    submissionEventData.enabledConnectors = connectors?.enabled;
    submissionEventData.disabledConnectors = connectors?.disabled;
    submissionEventData.referrer = document.referrer;
    submissionEventData.inlineAssistantAction = inlineAssistantAction;
    const attachedTabs = mentions?.filter((item) => item.type === 'tab' && item.url) ?? [];
    submissionEventData.attachedTabsDomains = [
        ...new Set(attachedTabs.map((item) => getTabHost(item.url)).filter(Boolean)),
    ];
    submissionEventData.attachedTabsCount = attachedTabs.length;
    const mentionedSpaces = mentions?.filter((item) => item.type === 'space') ?? [];
    submissionEventData.mentionedSpacesCount = mentionedSpaces.length;
    const mentionedShortcuts = mentions?.filter((item) => item.type === 'shortcut') ?? [];
    submissionEventData.mentionedShortcutsCount = mentionedShortcuts.length;
    submissionEventData.mentionedUsersCount = mentionedUsers.length;
    submissionEventDataMap.set(id, submissionEventData);
    track({
        event: 'submit query',
        data: submissionEventData,
    });
};
const getStartFirstServerResponseElapsed = (id) => {
    const submissionEventData = submissionEventDataMap.get(id);
    let startFirstServerResponseElapsed = -1;
    if (!submissionEventData) {
        return startFirstServerResponseElapsed;
    }
    startFirstServerResponseElapsed =
        submissionEventData.startFirstServerResponseMarker?.timeSinceTimestamp(submissionEventData.submitTimestamp) ?? -1;
    return startFirstServerResponseElapsed;
};
const getStartLLMTokenElapsed = (id) => {
    const submissionEventData = submissionEventDataMap.get(id);
    let startLLMTokenElapsed = -1;
    if (!submissionEventData) {
        return startLLMTokenElapsed;
    }
    startLLMTokenElapsed =
        submissionEventData.startLLMTokenMarker?.timeSinceTimestamp(submissionEventData.submitTimestamp) ?? -1;
    return startLLMTokenElapsed;
};
//# sourceMappingURL=submitQueryTracking.js.map